import React, { useEffect, useState, useMemo } from "react";
import { api } from "@/lib/api";
import { PowerballBall } from "./PowerballBall";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";

const DAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const weekdayOf = (iso) => DAYS[new Date(iso + "T12:00:00").getDay()];
const shortDate = (iso) => { const d = new Date(iso + "T12:00:00"); return `${MONTHS[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`; };

export function History() {
  const [draws, setDraws] = useState([]);
  const [q, setQ] = useState("");
  const [day, setDay] = useState("All");
  const [limit, setLimit] = useState(30);

  useEffect(() => {
    api.get("/draws?limit=500").then((r) => setDraws(r.data.draws));
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim();
    return draws.filter((d) => {
      if (day !== "All" && weekdayOf(d.date) !== day) return false;
      if (!s) return true;
      if (d.date.includes(s)) return true;
      const num = parseInt(s, 10);
      if (!isNaN(num)) return d.whites.includes(num) || d.powerball === num;
      return false;
    });
  }, [draws, q, day]);

  return (
    <section id="history" className="px-4 sm:px-8 lg:px-12 py-12 max-w-[1400px] mx-auto">
      <div className="flex items-end justify-between mb-6 gap-4 flex-wrap">
        <div>
          <div className="label-caps text-oracle">Archive</div>
          <h2 className="font-heading text-3xl sm:text-4xl mt-1 tracking-tight text-parchment">
            Draw <span className="italic text-oracle">History</span>
          </h2>
        </div>
        <div className="flex items-center gap-3 flex-wrap w-full sm:w-auto">
          <div className="flex rounded-md border border-white/10 overflow-hidden" data-testid="day-filter">
            {["All", "Mon", "Wed", "Sat"].map((d) => (
              <button
                key={d}
                data-testid={`day-filter-${d.toLowerCase()}`}
                onClick={() => { setDay(d); setLimit(30); }}
                className={`label-caps px-3.5 py-2.5 transition-colors ${day === d ? "bg-oracle text-ink" : "text-parchment/60 hover:text-oracle hover:bg-white/5"}`}
              >
                {d}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-parchment/40" strokeWidth={1.5} />
            <Input
              data-testid="history-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              inputMode="numeric"
              placeholder="Search date or number…"
              className="pl-9 h-11 sm:h-10 bg-surface border-white/10 text-parchment font-body focus-visible:ring-oracle"
            />
          </div>
        </div>
      </div>

      <div className="font-body text-xs text-parchment/50 mb-3" data-testid="history-count">
        Showing {Math.min(limit, filtered.length)} of {filtered.length} draws{day !== "All" ? ` on ${day}days` : ""}
      </div>

      <div className="bg-surface border border-white/5 rounded-xl overflow-hidden">
        <div className="hidden sm:grid grid-cols-12 label-caps px-6 py-3 border-b border-white/5">
          <div className="col-span-3">Draw Date</div>
          <div className="col-span-9">Numbers</div>
        </div>
        <ul>
          {filtered.slice(0, limit).map((d) => (
            <li key={d.date} className="flex flex-col gap-2 sm:grid sm:grid-cols-12 px-4 sm:px-6 py-3 border-b border-white/5 sm:items-center hover:bg-white/[0.02] transition-colors" data-testid={`draw-row-${d.date}`}>
              <div className="sm:col-span-3 font-body text-xs sm:text-sm flex items-baseline gap-2">
                <span className="text-parchment/80 sm:hidden">{shortDate(d.date)}</span>
                <span className="text-parchment/80 hidden sm:inline">{d.date}</span>
                <span className="label-caps text-oracle/80">{weekdayOf(d.date)}</span>
              </div>
              <div className="sm:col-span-9 flex flex-nowrap sm:flex-wrap gap-1.5 sm:gap-2 items-center">
                {d.whites.map((n) => (
                  <PowerballBall key={n} n={n} size="sm" />
                ))}
                <span className="text-parchment/30 mx-0.5 sm:mx-1">·</span>
                <PowerballBall n={d.powerball} red size="sm" />
              </div>
            </li>
          ))}
        </ul>
        {filtered.length > limit && (
          <div className="px-6 py-4 text-center">
            <button
              data-testid="load-more-btn"
              onClick={() => setLimit((l) => l + 30)}
              className="label-caps text-oracle hover:text-[#FFC033] transition-colors"
            >
              Load more ({filtered.length - limit} remaining)
            </button>
          </div>
        )}
        {filtered.length === 0 && (
          <div className="px-6 py-8 text-center font-body text-parchment/40">No draws match.</div>
        )}
      </div>
    </section>
  );
}
